import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { AddFormFieldButton } from '@/components/Buttons/AddFormFieldButton/AddFormFieldButton';
import { RemoveFormFieldButton } from '@/components/Buttons/RemoveFormFieldButton/RemoveFormFieldButton';
import { RequiredChip } from '@/components/Chips/RequiredChip';
import type { RequirePair } from '@/utils/typescript/RequirePair';

import { formTextFieldStyles as sx } from './FormTextField.styles';

type Props = {
	label: string;
	placeholder?: string;
	isRequired?: boolean;
	isDirty: boolean;
	indicateError: boolean;
	errorMessage?: string;
	defaultValue?: string;
	onChange?: (value: string) => void;
	disabled?: boolean;
	name?: string;
	id?: string;
	isDateField?: boolean;
} & RequirePair<'canAddNewItem', 'onAddNewItemClick', () => void> &
	RequirePair<'canBeRemoved', 'onRemove', () => void>;

export const FormTextField = ({
	label,
	placeholder = label,
	isRequired,
	isDirty,
	indicateError,
	errorMessage,
	canBeRemoved,
	defaultValue,
	onRemove,
	onChange,
	disabled,
	name,
	id,
	isDateField,
	canAddNewItem,
	onAddNewItemClick
}: Props) => {
	return (
		<Box sx={() => sx.root(!!canAddNewItem)}>
			<Typography component='label' htmlFor={id} sx={sx.label}>
				{label}
			</Typography>

			<Box>
				<Box sx={sx.fieldWrap}>
					<Box sx={sx.field}>
						<TextField
							id={id}
							name={name}
							placeholder={placeholder}
							defaultValue={defaultValue}
							helperText={errorMessage}
							error={!!errorMessage || indicateError}
							variant='outlined'
							type={isDateField ? 'date' : 'text'}
							size='small'
							fullWidth
							disabled={disabled}
							onChange={e => onChange?.(e.target.value)}
							sx={[
								sx.textField(!!indicateError, !!isDirty),
								isDateField ? sx.dateField : {}
							]}
							slotProps={{
								htmlInput: {
									'aria-required': !!isRequired
								}
							}}
						/>

						<Box sx={sx.badgeWrap}>
							{isRequired ? (
								<RequiredChip indicateError={indicateError} />
							) : null}
						</Box>
					</Box>

					{canBeRemoved && onRemove && (
						<RemoveFormFieldButton
							onClick={onRemove}
							customSx={sx.removeButton}
						/>
					)}
				</Box>

				{canAddNewItem && (
					<Box sx={sx.addNewItemButtonWrap}>
						<AddFormFieldButton onClick={onAddNewItemClick} />
					</Box>
				)}
			</Box>
		</Box>
	);
};
